import { LitElement, html, css } from "lit";
import { customElement, property } from "lit/decorators.js";
import type { MergedAttribute } from "../../services/entity-definition.service";
import type { ResolvedRelation } from "../../services/relation-resolver";
import { iconPlay, iconAudio, iconPaperclip, iconStar, iconDownload, iconExternal } from "../../utils/icons";
import { fileUrl, resolveUrl, getInitials, formatSize, normalizeFile, toMediaFile } from "../../utils/media";

export type ResolverFn = (entityName: string, id: string) => ResolvedRelation | undefined;

type MediaKind = "image" | "video" | "audio" | "pdf" | "file";

interface MediaInfo { url: string; filename: string; mimeType: string; size: number; kind: MediaKind; ext: string; }

const STATUS_COLORS: Record<string, { bg: string; fg: string }> = {
	ok: { bg: "rgba(107,187,36,0.12)", fg: "#3f7a12" },
	warn: { bg: "rgba(245,158,11,0.12)", fg: "#d97706" },
	error: { bg: "rgba(234,67,53,0.12)", fg: "#dc2626" },
	info: { bg: "rgba(90,157,30,0.08)", fg: "#5a9d1e" },
	default: { bg: "var(--clam-almost-white, #f0f0f0)", fg: "var(--clam-text-muted, #6d6e71)" },
};

function extOf(filename?: string, mimeType?: string): string {
	if (filename && filename.includes(".")) {
		return filename.split(".").pop()!.slice(0, 4).toUpperCase();
	}
	const byMime: Record<string, string> = {
		"application/pdf": "PDF",
		"application/zip": "ZIP",
		"application/vnd.ms-excel": "XLS",
		"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "XLS",
		"application/msword": "DOC",
		"application/vnd.openxmlformats-officedocument.wordprocessingml.document": "DOC",
	};
	if (mimeType && byMime[mimeType]) return byMime[mimeType];
	if (mimeType && mimeType.startsWith("image/")) return "IMG";
	if (mimeType && mimeType.startsWith("video/")) return "VID";
	if (mimeType && mimeType.startsWith("audio/")) return "AUD";
	return "FILE";
}

function colorFor(ext: string): string {
	const e = ext.toLowerCase();
	if (e === "pdf") return "#e53935";
	if (["doc", "docx", "rtf"].includes(e)) return "#2b7cd3";
	if (["xls", "xlsx", "csv"].includes(e)) return "#2e8b57";
	if (["ppt", "pptx"].includes(e)) return "#e8740c";
	if (["zip", "rar", "7z", "gz", "tar"].includes(e)) return "#7e57c2";
	if (["mp4", "mov", "avi", "mkv", "webm"].includes(e)) return "#7c4dff";
	if (["mp3", "wav", "ogg", "m4a", "flac", "aac"].includes(e)) return "#e8740c";
	if (["jpg", "jpeg", "png", "gif", "webp", "svg", "bmp"].includes(e)) return "#5a9d1e";
	return "#6d6e71";
}

function basename(u: string): string {
	try { return u.split("?")[0].split("/").pop() || u; } catch { return u; }
}

// MIME desde data-URI, objeto o URL.
function mimeOf(raw: unknown, url: string): string {
	if (raw && typeof raw === "object") {
		const mt = (raw as { mimeType?: string }).mimeType;
		if (mt) return mt;
	}
	if (url.startsWith("data:")) {
		const m = url.match(/^data:([^;,]+)/i);
		if (m) return m[1].toLowerCase();
	}
	const u = url.toLowerCase().split("?")[0];
	if (u.endsWith(".pdf")) return "application/pdf";
	if (u.endsWith(".mp4")) return "video/mp4";
	if (u.endsWith(".webm")) return "video/webm";
	if (u.endsWith(".mov")) return "video/quicktime";
	if (u.endsWith(".mp3")) return "audio/mpeg";
	if (u.endsWith(".wav")) return "audio/wav";
	if (u.endsWith(".ogg")) return "audio/ogg";
	if (u.endsWith(".m4a")) return "audio/mp4";
	if (u.endsWith(".png")) return "image/png";
	if (u.endsWith(".jpg") || u.endsWith(".jpeg")) return "image/jpeg";
	if (u.endsWith(".gif")) return "image/gif";
	if (u.endsWith(".webp")) return "image/webp";
	if (u.endsWith(".svg")) return "image/svg+xml";
	return "";
}

function classifyKind(mimeType: string): MediaKind {
	const mt = (mimeType || "").toLowerCase();
	if (mt.startsWith("image/")) return "image";
	if (mt.startsWith("video/")) return "video";
	if (mt.startsWith("audio/")) return "audio";
	if (mt === "application/pdf") return "pdf";
	return "file";
}

@customElement("clam-dynamic-viewer")
export class ClamDynamicViewer extends LitElement {
	static styles = css`
		:host { display: inline-block; font-size: var(--clam-text-sm, 13px); max-width: 240px; vertical-align: middle; }
		:host([detailed]) { display: block; max-width: 100%; }
		/* En modo detalle, los textos/URLs/tokens largos sin espacios forzan salto de línea
		   (en vez de nowrap+ellipsis que desbordaba el parent). Compacto (tabla) mantiene ellipsis. */
		:host([detailed]) .edv__text,
		:host([detailed]) .edv__link,
		:host([detailed]) .edv__relation {
			white-space: normal;
			overflow-wrap: anywhere;
			word-break: break-word;
			display: block;
			max-width: 100%;
		}
		.edv__text { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; display: inline-block; max-width: 100%; }
		.edv__link { color: var(--clam-green, #5a9d1e); text-decoration: none; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
		.edv__link:hover { text-decoration: underline; }
		.edv__badge { display: inline-block; padding: 2px 10px; border-radius: 9999px; font-size: var(--clam-text-xs, 11px); font-weight: 600; }
		.edv__badge--on { background: rgba(107,187,36,0.12); color: #3f7a12; }
		.edv__badge--off { background: var(--clam-surface-soft, #f5f6f8); color: var(--clam-light-gray, #898a8c); }
		.edv__status { display: inline-block; padding: 2px 10px; border-radius: 9999px; font-size: var(--clam-text-xs, 11px); font-weight: 600; }
		.edv__thumb { width: 40px; height: 40px; border-radius: 4px; object-fit: cover; border: 2px solid var(--clam-border, #e2e8f0); box-shadow: 0 1px 3px rgba(0,0,0,0.06); }
		.edv__thumb--rounded { border-radius: 50%; }
		.edv__avatar { width: 32px; height: 32px; border-radius: 50%; object-fit: cover; }
		.edv__avatar-fallback { width: 32px; height: 32px; border-radius: 50%; background: var(--clam-darkgray, #424244); color: var(--clam-white, #fff); display: inline-flex; align-items: center; justify-content: center; font-size: 13px; font-weight: 600; }
		.edv__chips { display: flex; flex-wrap: wrap; gap: 2px; }
		.edv__gallery { display: inline-flex; flex-wrap: wrap; gap: 4px; align-items: center; }
		.edv__chip { display: inline-flex; align-items: center; gap: 4px; padding: 2px 8px; border-radius: var(--clam-radius-sm, 6px); background: var(--clam-almost-white, #f0f0f0); color: var(--clam-text-muted, #555); font-size: var(--clam-text-xs, 11px); }
		.edv__relation { color: var(--clam-green, #5a9d1e); font-weight: 500; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
		.edv__muted { color: var(--clam-border, #ccc); }
		.edv__code { font-family: 'SF Mono', 'Fira Code', monospace; font-size: 11px; background: var(--clam-surface-soft, #f5f6f8); padding: 1px 4px; border-radius: 3px; word-break: break-all; }
		.edv__progress { width: 80px; height: 8px; background: var(--clam-surface-soft, #f5f6f8); border-radius: 9999px; overflow: hidden; display: inline-block; }
		.edv__progress-fill { height: 100%; background: var(--clam-light-green, #6bbb24); border-radius: 9999px; }
		.edv__stars { color: #f59e0b; letter-spacing: 1px; }

		/* === Media rich preview (detailed) === */
		.edv__media { display: flex; flex-wrap: wrap; gap: 8px; }
		.edv__img-wrap { position: relative; display: inline-block; line-height: 0; border-radius: var(--clam-radius-sm, 8px); overflow: hidden; border: 1px solid var(--clam-border, #e2e8f0); }
		.edv__img-wrap a { line-height: 0; display: block; }
		.edv__img { display: block; object-fit: cover; background: var(--clam-surface-soft, #f0f2f5); }
		.edv__dims { position: absolute; left: 0; bottom: 0; background: rgba(0,0,0,0.62); color: #fff; font-size: 10px; font-weight: 600; line-height: 1; padding: 3px 6px; border-top-right-radius: 6px; letter-spacing: 0.02em; }
		.edv__video { display: block; max-width: 360px; width: 100%; border-radius: var(--clam-radius-sm, 8px); border: 1px solid var(--clam-border, #e2e8f0); background: #000; }
		.edv__audio-row { display: flex; align-items: center; gap: 8px; max-width: 360px; width: 100%; }
		.edv__audio-row audio { flex: 1; min-width: 0; height: 34px; }
		.edv__filecard { display: flex; align-items: center; gap: 10px; padding: 8px 10px; border: 1px solid var(--clam-border, #e2e8f0); border-radius: var(--clam-radius-sm, 8px); background: var(--clam-surface, #fff); max-width: 420px; box-sizing: border-box; }
		.edv__filecard:hover { box-shadow: 0 1px 3px rgba(0,0,0,0.06); }
		.edv__filebadge { width: 36px; height: 44px; border-radius: 5px; background: var(--c, #6d6e71); color: #fff; display: flex; align-items: flex-end; justify-content: center; padding-bottom: 5px; font-size: 9px; font-weight: 800; position: relative; flex-shrink: 0; text-decoration: none; }
		.edv__filebadge::after { content: ""; position: absolute; top: 0; right: 0; border-style: solid; border-width: 0 9px 9px 0; border-color: transparent rgba(255,255,255,0.4) transparent transparent; }
		.edv__fileinfo { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 1px; }
		.edv__filename { font-size: var(--clam-text-sm, 13px); font-weight: 600; color: var(--clam-text, #333); text-decoration: none; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
		.edv__filename:hover { color: var(--clam-green, #5a9d1e); }
		.edv__filemeta { font-size: 11px; color: var(--clam-text-muted, #9ca3af); }
		.edv__fileacts { display: flex; gap: 2px; flex-shrink: 0; }
		.edv__fileact { display: inline-flex; align-items: center; justify-content: center; width: 30px; height: 30px; border-radius: var(--clam-radius-sm, 6px); border: none; cursor: pointer; text-decoration: none; background: transparent; color: var(--clam-text-muted, #6d6e71); font-size: 14px; }
		.edv__fileact:hover { background: var(--clam-surface-soft, #f0f2f5); color: var(--clam-text, #333); }

		/* === Objetos/arreglos anidados (detalle) === */
		.edv__obj-grid { display: grid; grid-template-columns: 1fr; gap: 2px; border: 1px solid var(--clam-border, #e2e8f0); border-radius: var(--clam-radius-sm, 6px); overflow: hidden; }
		.edv__obj-row { display: grid; grid-template-columns: 130px minmax(0, 1fr); gap: 8px; padding: 5px 8px; font-size: 12px; border-bottom: 1px solid var(--clam-almost-white, #f0f0f0); }
		.edv__obj-row:last-child { border-bottom: none; }
		.edv__obj-row:nth-child(odd) { background: var(--clam-surface-soft, #f8fafc); }
		.edv__obj-key { color: var(--clam-text-muted, #6d6e71); font-weight: 600; text-transform: capitalize; overflow-wrap: anywhere; }
		.edv__obj-val { color: var(--clam-text, #333); overflow-wrap: anywhere; word-break: break-word; min-width: 0; }
		.edv__arr-count { display: inline-block; font-size: 11px; font-weight: 600; color: var(--clam-text-muted, #6d6e71); background: var(--clam-surface-soft, #f5f6f8); padding: 2px 8px; border-radius: 9999px; margin-bottom: 6px; }
		.edv__arr-list { display: flex; flex-direction: column; gap: 8px; max-height: 280px; overflow-y: auto; padding-right: 4px; }
		.edv__arr-item { border: 1px solid var(--clam-border, #e2e8f0); border-radius: var(--clam-radius-sm, 6px); padding: 6px; }
		.edv__arr-head { font-size: 10px; font-weight: 700; color: var(--clam-text-muted, #9ca3af); text-transform: uppercase; letter-spacing: 0.04em; margin-bottom: 4px; }

		/* === Relación expandida (detalle) con scroll === */
		.edv__relation-card { display: inline-flex; flex-direction: column; gap: 2px; background: var(--clam-surface-soft, #f5f6f8); border: 1px solid var(--clam-border, #e2e8f0); border-radius: var(--clam-radius-sm, 6px); padding: 6px 10px; max-width: 100%; }
		.edv__relation-card .edv__relation-main { color: var(--clam-green, #5a9d1e); font-weight: 600; font-size: 13px; overflow-wrap: anywhere; }
		.edv__relation-card .edv__relation-sub { font-size: 11px; color: var(--clam-text-muted, #6d6e71); overflow-wrap: anywhere; }
		.edv__relation-many { display: flex; flex-wrap: wrap; gap: 4px; max-height: 200px; overflow-y: auto; }
	`;

	@property({ attribute: false }) attr!: MergedAttribute;
	@property({ attribute: false }) value: unknown = null;
	@property({ attribute: false }) resolver?: ResolverFn;
	@property({ type: Boolean, reflect: true }) detailed = false;

	/* dimensiones de imágenes cargadas (key=url -> "WxH") */
	private _dims = new Map<string, string>();

	private get fmtOpts(): Record<string, unknown> {
		return (this.attr as unknown as { formatOptions?: Record<string, unknown> }).formatOptions ?? {};
	}

	private mediaInfo(raw: unknown): MediaInfo | null {
		const mf = this.attr.fileKeys ? toMediaFile(raw, this.attr.fileKeys) : normalizeFile(raw);
		if (!mf) return null;
		const url = fileUrl(mf);
		if (!url) return null;
		const mimeType = mimeOf(mf, url);
		const filename = mf.filename || basename(url);
		const kind = classifyKind(mimeType);
		return { url, filename, mimeType, kind, ext: extOf(filename, mimeType), size: mf.size || 0 };
	}

	private _onImgLoad = (e: Event, key: string) => {
		const img = e.target as HTMLImageElement;
		if (img.naturalWidth) {
			this._dims.set(key, `${img.naturalWidth}×${img.naturalHeight}px`);
			this.requestUpdate();
		}
	};

	render() {
		if (this.value === null || this.value === undefined || this.value === "") {
			return html`<span class="edv__muted">—</span>`;
		}

		switch (this.attr.effectiveOutputType) {
			case "badge": return this.renderBadge();
			case "status": return this.renderStatus();
			case "image":
			case "video":
			case "file":
				return this.renderMedia(this.value);
			case "avatar": return this.renderAvatar();
			case "link": return this.renderLink();
			case "date":
			case "datetime": return this.renderDate();
			case "chips": return this.renderChips();
			case "relation": return this.renderRelation();
			case "relationMany": return this.renderRelationMany();
			case "objectSummary": return this.renderObjectSummary();
			case "arrayCount": return this.renderArrayCount();
			case "code":
			case "mono": return html`<code class="edv__code">${String(this.value)}</code>`;
			case "progressBar": return this.renderProgress();
			case "rating": return this.renderRating();
			default: {
				if (this.value && typeof this.value === "object") {
					// En modo detalle, expandir objetos/arreglos anidados (antes colapsaban a {…}/N ítems).
					if (this.detailed) {
						if (Array.isArray(this.value)) return this.renderArrayDetail(this.value);
						return this.renderObjectDetail(this.value as Record<string, unknown>);
					}
					const v = this.value as Record<string, unknown>;
					const str = Array.isArray(v) ? `${v.length} ítems` : (Object.keys(v).length ? "{…}" : "—");
					return html`<span class="edv__muted">${str}</span>`;
				}
				return html`<span class="edv__text" title=${String(this.value)}>${String(this.value)}</span>`;
			}
		}
	}

	/* === Objetos/arreglos anidados expandidos (detalle) === */
	private static readonly INTERNAL_KEYS = new Set(["id", "deleted", "key", "createdOn", "modifiedOn", "_order", "parentId", "parent_id"]);

	private renderScalar(v: unknown): unknown {
		if (v === null || v === undefined || v === "") return html`<span class="edv__muted">—</span>`;
		if (Array.isArray(v)) return html`<span>${v.length} ítems</span>`;
		if (typeof v === "object") return html`<span class="edv__muted">{…}</span>`;
		if (typeof v === "string" && /^(https?:|data:)/i.test(v) && v.length < 400) return html`<a class="edv__link" href=${v} target="_blank" rel="noopener">${v}</a>`;
		return html`<span>${String(v)}</span>`;
	}

	private renderObjectDetail(obj: Record<string, unknown>): unknown {
		const entries = Object.entries(obj).filter(([k, v]) => !ClamDynamicViewer.INTERNAL_KEYS.has(k) && v !== null && v !== undefined && v !== "");
		if (!entries.length) return html`<span class="edv__muted">—</span>`;
		return html`<div class="edv__obj-grid">${entries.map(([k, v]) => html`
			<div class="edv__obj-row"><span class="edv__obj-key">${k}</span><span class="edv__obj-val">${this.renderScalar(v)}</span></div>
		`)}</div>`;
	}

	private renderArrayDetail(arr: unknown[]): unknown {
		if (!arr.length) return html`<span class="edv__muted">—</span>`;
		return html`<div class="edv__arr">
			<span class="edv__arr-count">${arr.length} ítems</span>
			<div class="edv__arr-list">${arr.map((item, i) => html`
				<div class="edv__arr-item">
					<div class="edv__arr-head">#${i + 1}</div>
					${item && typeof item === "object" ? this.renderObjectDetail(item as Record<string, unknown>) : html`<span class="edv__obj-val">${this.renderScalar(item)}</span>`}
				</div>
			`)}</div>
		</div>`;
	}

	/* === MEDIA (imagen / video / audio / pdf / file) === */
	private renderMedia(value: unknown) {
		const arr = Array.isArray(value) ? value : null;
		if (arr) {
			if (arr.length === 0) return html`<span class="edv__muted">—</span>`;
			if (this.detailed) {
				return html`<div class="edv__media">${arr.map((v) => this.renderMediaOne(v))}</div>`;
			}
			const max = (this.fmtOpts.maxVisible as number) ?? 3;
			const visible = arr.slice(0, max);
			const overflow = arr.length - max;
			return html`<span class="edv__gallery">${visible.map((v) => this.renderCompactThumb(v))}${overflow > 0 ? html`<span class="edv__chip">+${overflow}</span>` : null}</span>`;
		}
		return this.renderMediaOne(value);
	}

	private renderCompactThumb(value: unknown) {
		const info = this.mediaInfo(value);
		if (!info) return html`<span class="edv__muted">—</span>`;
		if (info.kind === "image") {
			const size = (this.fmtOpts.size as number) ?? 40;
			const rounded = this.fmtOpts.rounded as boolean;
			return html`<a href=${info.url} target="_blank" rel="noopener" title=${info.filename} @click=${(e: Event) => e.stopPropagation()}><img class="edv__thumb ${rounded ? "edv__thumb--rounded" : ""}" src=${info.url} alt="" style="width:${size}px;height:${size}px" /></a>`;
		}
		// no-imagen en compact: chip con ext (evita <img> roto en PDFs, etc.)
		return html`<a class="edv__chip" href=${info.url} target="_blank" rel="noopener" title=${info.filename} @click=${(e: Event) => e.stopPropagation()}>${info.kind === "video" ? iconPlay() : info.kind === "audio" ? iconAudio() : iconPaperclip()} ${info.ext}</a>`;
	}

	private renderMediaOne(value: unknown) {
		const info = this.mediaInfo(value);
		if (!info) return html`<span class="edv__muted">—</span>`;

		if (!this.detailed) return this.renderCompactThumb(value);

		// === detailed ===
		switch (info.kind) {
			case "image": return this.renderDetailedImage(info);
			case "video": return this.renderVideo(info);
			case "audio": return this.renderAudio(info);
			default: return this.renderFileCard(info); // pdf | file
		}
	}

	private renderDetailedImage(info: MediaInfo) {
		const size = (this.fmtOpts.size as number) ?? 220;
		const cap = Math.min(size, 280);
		const key = info.url;
		return html`
			<div class="edv__img-wrap" style="width:${cap}px">
				<a href=${info.url} target="_blank" rel="noopener" title=${info.filename} @click=${(e: Event) => e.stopPropagation()}>
					<img class="edv__img" src=${info.url} alt=${info.filename} style="width:${cap}px;height:${cap}px"
						@load=${(e: Event) => this._onImgLoad(e, key)} />
				</a>
				${this._dims.has(key) ? html`<span class="edv__dims">${this._dims.get(key)}</span>` : null}
			</div>
		`;
	}

	private renderVideo(info: MediaInfo) {
		return html`
			<div style="display:flex;flex-direction:column;gap:4px;max-width:360px">
				<video class="edv__video" src=${info.url} controls preload="metadata" @click=${(e: Event) => e.stopPropagation()}></video>
				<span class="edv__filemeta">${info.filename}${info.size ? " · " + formatSize(info.size) : ""}</span>
			</div>
		`;
	}

	private renderAudio(info: MediaInfo) {
		return html`
			<div class="edv__audio-row">
				<audio src=${info.url} controls preload="metadata" @click=${(e: Event) => e.stopPropagation()}></audio>
				<a class="edv__fileact" href=${info.url} target="_blank" rel="noopener" download title="Descargar" @click=${(e: Event) => e.stopPropagation()}>${iconDownload()}</a>
			</div>
		`;
	}

	private renderFileCard(info: MediaInfo) {
		return html`
			<div class="edv__filecard">
				<a class="edv__filebadge" style="--c:${colorFor(info.ext)}" href=${info.url} target="_blank" rel="noopener" title="Abrir" @click=${(e: Event) => e.stopPropagation()}>${info.ext}</a>
				<div class="edv__fileinfo">
					<a class="edv__filename" href=${info.url} target="_blank" rel="noopener" title=${info.filename} @click=${(e: Event) => e.stopPropagation()}>${info.filename || (info.kind === "pdf" ? "Documento PDF" : "Archivo")}</a>
					<span class="edv__filemeta">${[info.size ? formatSize(info.size) : null, info.ext, info.mimeType].filter(Boolean).join(" · ")}</span>
				</div>
			<div class="edv__fileacts">
				<a class="edv__fileact" href=${info.url} target="_blank" rel="noopener" title="Abrir" @click=${(e: Event) => e.stopPropagation()}>${iconExternal()}</a>
				<a class="edv__fileact" href=${info.url} target="_blank" rel="noopener" download title="Descargar" @click=${(e: Event) => e.stopPropagation()}>${iconDownload()}</a>
				</div>
			</div>
		`;
	}

	private renderBadge() {
		const onLabel = (this.fmtOpts.onLabel as string) || "Sí";
		const offLabel = (this.fmtOpts.offLabel as string) || "No";
		return this.value
			? html`<span class="edv__badge edv__badge--on">${onLabel}</span>`
			: html`<span class="edv__badge edv__badge--off">${offLabel}</span>`;
	}

	private renderStatus() {
		const val = String(this.value);
		const colorMap = (this.fmtOpts.colorMap ?? {}) as Record<string, string>;
		const variant = colorMap[val.toLowerCase()] ?? colorMap[val] ?? "default";
		const c = STATUS_COLORS[variant] ?? STATUS_COLORS.default;
		return html`<span class="edv__status" style="background:${c.bg};color:${c.fg}">${val}</span>`;
	}

	private renderAvatar() {
		const mf = this.attr.fileKeys ? toMediaFile(this.value, this.attr.fileKeys) : normalizeFile(this.value);
		const url = fileUrl(mf);
		const size = (this.fmtOpts.size as number) ?? 32;
		if (url) return html`<img class="edv__avatar" src=${url} alt="" style="width:${size}px;height:${size}px" />`;
		const name = String(this.value);
		return html`<span class="edv__avatar-fallback" style="width:${size}px;height:${size}px;font-size:${Math.floor(size * 0.4)}px">${getInitials(name)}</span>`;
	}

	private renderLink() {
		const url = String(this.value);
		const openNew = this.fmtOpts.openInNewTab !== false;
		return html`<a class="edv__link" href=${url} target=${openNew ? "_blank" : "_self"} rel=${openNew ? "noopener" : ""}>${url}</a>`;
	}

	private renderDate() {
		try {
			const d = new Date(String(this.value));
			if (this.attr.effectiveOutputType === "datetime") {
				return html`<span>${d.toLocaleString("es-MX", { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</span>`;
			}
			return html`<span>${d.toLocaleDateString("es-MX", { year: "numeric", month: "short", day: "numeric" })}</span>`;
		} catch {
			return html`<span>${String(this.value)}</span>`;
		}
	}

	private renderChips() {
		const arr = Array.isArray(this.value) ? this.value : [this.value];
		const max = (this.fmtOpts.maxVisible as number) ?? 3;
		const visible = arr.slice(0, max);
		const overflow = arr.length - max;
		return html`<span class="edv__chips">${visible.map((v) => html`<span class="edv__chip">${String(v)}</span>`)}${overflow > 0 ? html`<span class="edv__chip">+${overflow}</span>` : ""}</span>`;
	}

	private renderRelation() {
		if (!this.attr.relatedWith || !this.resolver) return html`<span class="edv__text">${String(this.value)}</span>`;
		const resolved = this.resolver(this.attr.relatedWith, String(this.value));
		if (!resolved) return html`<span class="edv__muted">${String(this.value).substring(0, 8)}</span>`;
		if (this.detailed) {
			return html`<div class="edv__relation-card">
				<span class="edv__relation-main">${resolved.label}</span>
				${resolved.secondary && resolved.secondary.length ? html`<span class="edv__relation-sub">${resolved.secondary.join(" · ")}</span>` : null}
			</div>`;
		}
		return html`<span class="edv__relation" title=${resolved.secondary?.join(" · ") || ""}>${resolved.label}</span>`;
	}

	private renderRelationMany() {
		if (!this.attr.relatedWith || !this.resolver) {
			const count = Array.isArray(this.value) ? this.value.length : 0;
			return html`<span>${count} items</span>`;
		}
		const ids = Array.isArray(this.value) ? this.value : [this.value];
		const resolved = ids.map((id) => this.resolver!(this.attr.relatedWith!, String(id))).filter(Boolean) as { label: string; secondary?: string[] }[];
		if (this.detailed) {
			if (!resolved.length) return html`<span class="edv__muted">—</span>`;
			return html`<div class="edv__relation-many">${resolved.map((r) => html`
				<div class="edv__relation-card">
					<span class="edv__relation-main">${r.label}</span>
					${r.secondary && r.secondary.length ? html`<span class="edv__relation-sub">${r.secondary.join(" · ")}</span>` : null}
				</div>
			`)}</div>`;
		}
		const labels = resolved.map((r) => r.label);
		return html`<span class="edv__chips">${labels.slice(0, 2).map((l) => html`<span class="edv__chip">${l}</span>`)}${labels.length > 2 ? html`<span class="edv__chip">+${labels.length - 2}</span>` : ""}</span>`;
	}

	private renderObjectSummary() {
		if (typeof this.value !== "object" || this.value === null) return html`<span>${String(this.value)}</span>`;
		const obj = this.value as Record<string, unknown>;
		const INTERNAL = new Set(["id", "deleted", "key", "createdOn", "modifiedOn", "_order", "parentId", "parent_id", "url"]);
		const displayKeys = (this.fmtOpts.displayKeys as string[])
			|| Object.keys(obj).filter((k) => !INTERNAL.has(k) && (typeof obj[k] === "string" || typeof obj[k] === "number")).slice(0, 3);
		const parts = displayKeys.map((k) => {
			const v = obj[k];
			if (v === null || v === undefined || v === "") return "";
			if (Array.isArray(v)) return `${v.length} ítems`;
			if (typeof v === "object") return "";
			return String(v).substring(0, 40);
		}).filter(Boolean);
		if (parts.length === 0) return html`<span class="edv__muted">—</span>`;
		const joined = parts.join(" · ");
		return html`<span class="edv__text" title=${joined}>${joined}</span>`;
	}

	private renderArrayCount() {
		const count = Array.isArray(this.value) ? this.value.length : 0;
		const label = (this.fmtOpts.label as string) || this.attr.attributeName?.toLowerCase() || "items";
		return html`<span>${count} ${label}</span>`;
	}

	private renderProgress() {
		const val = Number(this.value) || 0;
		const max = (this.fmtOpts.max as number) ?? 100;
		const pct = Math.min(100, (val / max) * 100);
		return html`<span class="edv__progress"><span class="edv__progress-fill" style="width:${pct}%"></span></span>`;
	}

	private renderRating() {
		const val = Number(this.value) || 0;
		const max = (this.fmtOpts.max as number) ?? 5;
		return html`<span class="edv__stars">${Array.from({ length: max }, (_, i) => (i < val ? iconStar(true) : iconStar(false)))}</span>`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"clam-dynamic-viewer": ClamDynamicViewer;
	}
}
